import * as React from "react"
import { cn } from "../../lib/utils"
import {
    MASK_PLACEHOLDER_DATE,
    MASK_PLACEHOLDER_DATETIME,
    MASK_PLACEHOLDER_TIME,
} from "../../constants/maskPlaceholders"

const defaultPlaceholderByMask = {
    date: MASK_PLACEHOLDER_DATE,
    time: MASK_PLACEHOLDER_TIME,
    datetime: MASK_PLACEHOLDER_DATETIME,
}

/**
 * MaskedInput - Input com máscaras automáticas
 * 
 * @param {string} mask - Tipo de máscara: 'cpf', 'cnpj', 'cpf-cnpj', 'phone', 'cep', 'date'
 * @param {string} value - Valor do input
 * @param {Function} onChange - Callback de mudança (retorna valor sem máscara)
 */

const masks = {
    cpf: (value) => {
        if (!value) return '';
        return value
            .replace(/\D/g, '')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})/, '$1-$2')
            .replace(/(-\d{2})\d+?$/, '$1');
    },
    cnpj: (value) => {
        if (!value) return '';
        return value
            .replace(/\D/g, '')
            .replace(/(\d{2})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1/$2')
            .replace(/(\d{4})(\d)/, '$1-$2')
            .replace(/(-\d{2})\d+?$/, '$1');
    },
    'cpf-cnpj': (value) => {
        if (!value) return '';
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 11) {
            return masks.cpf(value);
        }
        return masks.cnpj(value);
    },
    phone: (value) => {
        if (!value) return '';
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 10) {
            // Telefone fixo: (00) 0000-0000
            return value
                .replace(/\D/g, '')
                .replace(/(\d{2})(\d)/, '($1) $2')
                .replace(/(\d{4})(\d)/, '$1-$2')
                .replace(/(-\d{4})\d+?$/, '$1');
        }
        // Celular: (00) 00000-0000
        return value
            .replace(/\D/g, '')
            .replace(/(\d{2})(\d)/, '($1) $2')
            .replace(/(\d{5})(\d)/, '$1-$2')
            .replace(/(-\d{4})\d+?$/, '$1');
    },
    cep: (value) => {
        if (!value) return '';
        return value
            .replace(/\D/g, '')
            .replace(/(\d{5})(\d)/, '$1-$2')
            .replace(/(-\d{3})\d+?$/, '$1');
    },
    date: (value) => {
        if (!value) return '';
        return value
            .replace(/\D/g, '')
            .replace(/(\d{2})(\d)/, '$1/$2')
            .replace(/(\d{2})(\d)/, '$1/$2')
            .replace(/(\d{4})\d+?$/, '$1');
    },
    time: (value) => {
        if (!value) return '';
        return value
            .replace(/\D/g, '')
            .replace(/(\d{2})(\d)/, '$1:$2')
            .replace(/(\d{2}:\d{2})\d+?$/, '$1');
    },
    datetime: (value) => {
        if (!value) return '';
        // Formato: dd/mm/yyyy hh:mm
        const cleaned = value.replace(/\D/g, '');
        if (cleaned.length <= 8) {
            // Ainda está na parte da data
            return cleaned
                .replace(/(\d{2})(\d)/, '$1/$2')
                .replace(/(\d{2})(\d)/, '$1/$2')
                .replace(/(\d{4})\d+?$/, '$1');
        }
        // Já tem data completa, adicionar hora
        const datePart = cleaned.slice(0, 8);
        const timePart = cleaned.slice(8);
        const formattedDate = datePart
            .replace(/(\d{2})(\d)/, '$1/$2')
            .replace(/(\d{2})(\d)/, '$1/$2');
        if (timePart.length === 0) {
            return formattedDate;
        }
        const formattedTime = timePart
            .replace(/(\d{2})(\d)/, '$1:$2')
            .replace(/(\d{2}:\d{2})\d+?$/, '$1');
        return `${formattedDate} ${formattedTime}`;
    },
};

/** Formata um valor para exibição (ex.: formatMasked('phone', '11987654321')). */
export const formatMasked = (mask, value) => (value && masks[mask] ? masks[mask](String(value)) : '');

const maxLengths = {
    cpf: 14,        // 000.000.000-00
    cnpj: 18,       // 00.000.000/0000-00
    'cpf-cnpj': 18, // Máximo (CNPJ)
    phone: 15,      // (00) 00000-0000
    cep: 9,         // 00000-000
    date: 10,       // dd/mm/yyyy
    time: 5,        // hh:mm
    datetime: 16,   // dd/mm/yyyy hh:mm
};

const MaskedInput = React.forwardRef(({
    className,
    mask,
    value,
    onChange,
    placeholder,
    ...props
}, ref) => {
    const resolvedPlaceholder =
        placeholder !== undefined && placeholder !== null
            ? placeholder
            : (mask && defaultPlaceholderByMask[mask] ? defaultPlaceholderByMask[mask] : undefined);

    const applyMask = (inputValue) => {
        if (!inputValue) return '';
        if (!mask || !masks[mask]) return inputValue;
        return masks[mask](inputValue);
    };

    const handleChange = (e) => {
        const inputValue = e.target.value || '';
        const maskedValue = applyMask(inputValue);

        let valueToReturn = maskedValue.replace(/\D/g, '');

        // Para data, converter de dd/mm/yyyy para yyyy-mm-dd
        if (mask === 'date' && maskedValue.length === 10) {
            const [day, month, year] = maskedValue.split('/');
            valueToReturn = `${year}-${month}-${day}`;
        }
        // Para time, converter de hh:mm para hh:mm (formato ISO)
        else if (mask === 'time' && maskedValue.length === 5) {
            valueToReturn = maskedValue; // Mantém formato hh:mm
        }
        // Para datetime, converter de dd/mm/yyyy hh:mm para yyyy-mm-ddThh:mm
        else if (mask === 'datetime' && maskedValue.length >= 16) {
            const [datePart, timePart] = maskedValue.split(' ');
            if (datePart && timePart) {
                const [day, month, year] = datePart.split('/');
                valueToReturn = `${year}-${month}-${day}T${timePart}`;
            }
        }

        // Cria evento customizado com valor sem máscara
        const event = {
            ...e,
            target: {
                ...e.target,
                name: e.target.name,
                value: valueToReturn, // Valor sem máscara ou formato ISO para date/time/datetime
                maskedValue: maskedValue, // Valor com máscara (se precisar)
            },
        };

        onChange && onChange(event);
    };

    // Converter valor ISO para formato de exibição
    let displayValue = value;
    if (value) {
        if (mask === 'date') {
            // Converter yyyy-mm-dd para dd/mm/yyyy
            const dateOnly = value.split('T')[0];
            if (dateOnly.includes('-')) {
                const parts = dateOnly.split('-');
                if (parts.length === 3) {
                    const [year, month, day] = parts;
                    displayValue = `${day}${month}${year}`;
                }
            }
        } else if (mask === 'time') {
            // Time já vem no formato hh:mm, apenas remover caracteres não numéricos para aplicar máscara
            displayValue = value.replace(/[^\d]/g, '');
        } else if (mask === 'datetime') {
            // Converter yyyy-mm-ddThh:mm para dd/mm/yyyy hh:mm
            const [datePart, timePart] = value.split('T');
            if (datePart && timePart) {
                const parts = datePart.split('-');
                if (parts.length === 3) {
                    const [year, month, day] = parts;
                    // Remover caracteres não numéricos do timePart e concatenar
                    const timeDigits = timePart.replace(/[^\d]/g, '');
                    displayValue = `${day}${month}${year}${timeDigits}`;
                }
            } else if (datePart && !timePart) {
                // Apenas data, sem hora
                const parts = datePart.split('-');
                if (parts.length === 3) {
                    const [year, month, day] = parts;
                    displayValue = `${day}${month}${year}`;
                }
            }
        }
    }
    displayValue = applyMask(displayValue) || '';

    return (
        <input
            type="text"
            className={cn(
                "flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50",
                className
            )}
            ref={ref}
            value={displayValue}
            onChange={handleChange}
            maxLength={maxLengths[mask]}
            placeholder={resolvedPlaceholder}
            {...props}
        />
    )
})
MaskedInput.displayName = "MaskedInput"

export { MaskedInput }

